import { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  Link as RouterLink,
} from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import { useQuery } from "@tanstack/react-query";

import CustomerPortalLayout from "components/storefront/CustomerPortalLayout";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useAuth } from "context/AuthContext";
import { get } from "lib/api";
import { isDistributorRole, isPortalRole } from "lib/portalRoles";
import useStorefrontCopy from "hooks/useStorefrontCopy";

function prettyStatus(value) {
  return String(value || "not_started")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function normalizeEntity(row) {
  if (!row) return null;
  if (row?.data) return normalizeEntity(row.data);
  if (row?.attributes)
    return {
      id: row.id,
      documentId: row.documentId || row.id,
      ...row.attributes,
    };
  return row;
}

function claimStatusColor(status) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  if (normalized === "approved" || normalized === "closed") return "success";
  if (normalized === "denied") return "error";
  if (normalized === "received" || normalized === "testing") return "info";
  return "warning";
}

function formatDateTime(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
}

function formatMoney(value, currency) {
  const amount = Number(value || 0);
  const code = String(currency || "USD").toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
    }).format(amount);
  } catch {
    return `${code} ${amount.toFixed(2)}`;
  }
}

function nonEmpty(value) {
  const text = String(value || "").trim();
  return text || "";
}

function normalizeIssueCodes(value) {
  if (!Array.isArray(value)) return [];
  return value.map((code) => nonEmpty(code)).filter(Boolean);
}

function coreReturnStatusLabel(lifecycle) {
  const status = String(
    lifecycle?.activeReturnRequest?.status ||
      lifecycle?.activeReturnStatus ||
      ""
  )
    .trim()
    .toLowerCase();
  if (status === "forfeited" || lifecycle?.forfeitedAt) return "Forfeited";
  if (status === "inspection_pending") return "Received - warehouse inspection";
  if (status === "received") return "Received - awaiting inspection";
  if (status === "submitted") return "In transit";
  if (status === "label_generated") return "Label generated";
  if (status === "requested") return "Return started";
  if (status === "approved" || status === "closed" || lifecycle?.coreReturned)
    return "Approved";
  if (status === "cancelled") return "Cancelled";
  if (lifecycle?.coreDue) return "Core due";
  return "No active return";
}

function workflowStepsForClaim(claim) {
  const row = claim || {};
  const returnRequest = normalizeEntity(row?.returnRequest);
  const status = String(row?.status || "")
    .trim()
    .toLowerCase();

  return [
    {
      key: "submitted",
      label: "Claim submitted",
      at: row?.submittedAt || row?.createdAt || null,
      complete: !!(row?.submittedAt || row?.createdAt),
    },
    {
      key: "label",
      label: "Return label generated",
      at: returnRequest?.requestedAt || returnRequest?.createdAt || null,
      complete: !!returnRequest?.documentId,
    },
    {
      key: "customerShipped",
      label: "Customer shipped unit",
      at: row?.returnShippedAt || null,
      complete:
        !!row?.returnShippedAt ||
        String(returnRequest?.status || "").toLowerCase() === "submitted",
    },
    {
      key: "received",
      label: "Unit received",
      at: row?.returnReceivedAt || null,
      complete:
        !!row?.returnReceivedAt ||
        ["received", "testing", "approved", "denied", "closed"].includes(
          status
        ) ||
        String(returnRequest?.status || "").toLowerCase() === "received",
    },
    {
      key: "review",
      label: "Review / testing",
      at: row?.reviewStartedAt || null,
      complete:
        !!row?.reviewStartedAt ||
        ["testing", "approved", "denied", "closed"].includes(status),
    },
    {
      key: "decision",
      label: "Decision made",
      at: row?.approvedAt || row?.deniedAt || null,
      complete:
        !!row?.approvedAt ||
        !!row?.deniedAt ||
        ["approved", "denied", "closed"].includes(status),
    },
    {
      key: "outbound",
      label: "Replacement shipped to customer",
      at: row?.outboundShippedAt || null,
      complete: !!row?.outboundShippedAt,
    },
    {
      key: "closed",
      label: "Claim closed",
      at: row?.closedAt || row?.outboundDeliveredAt || null,
      complete: status === "closed" || !!row?.closedAt,
    },
  ];
}

function TinyRow({ label, value }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
      <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
        {label}
      </MDTypography>
      <MDTypography variant="button" fontWeight={700}>
        {value || "—"}
      </MDTypography>
    </Box>
  );
}

TinyRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

TinyRow.defaultProps = {
  value: "",
};

export default function CustomerValveBodyDetail() {
  const { token, pluginRole, authLoading, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { documentId } = useParams();
  const { t } = useStorefrontCopy();
  const role = String(pluginRole || "").toLowerCase();
  const isPortal = isPortalRole(role);
  const distributor = isDistributorRole(role);

  const detailQuery = useQuery({
    queryKey: ["storefront-valve-body", documentId],
    queryFn: () => get(`/api/storefront/valve-bodies/${documentId}`),
    enabled: !!token && !!documentId && isPortal,
    staleTime: 15000,
  });

  const historyQuery = useQuery({
    queryKey: ["storefront-valve-body-warranty-history", documentId],
    queryFn: () =>
      get(`/api/storefront/valve-bodies/${documentId}/warranty-history`),
    enabled: !!token && !!documentId && isPortal && !distributor,
    staleTime: 15000,
  });

  const detail = detailQuery.data?.data || null;
  const lifecycle = detail?.lifecycle || {};
  const activeWarrantyDocumentId = String(
    lifecycle?.activeWarranty?.documentId || ""
  ).trim();
  const activeWarrantyStatus = String(lifecycle?.activeWarranty?.status || "")
    .trim()
    .toLowerCase();
  const hasActiveWarranty =
    !!activeWarrantyStatus &&
    !["closed", "denied"].includes(activeWarrantyStatus);
  const warrantyHistory = useMemo(
    () =>
      Array.isArray(historyQuery.data?.data) ? historyQuery.data.data : [],
    [historyQuery.data]
  );
  const meQuery = useQuery({
    queryKey: ["storefront-me", "capabilities", "detail"],
    queryFn: () => get("/api/storefront/me"),
    enabled: !!token && isPortal,
    staleTime: 15000,
  });
  const capabilities = meQuery.data?.capabilities || {};
  const canSubmitCore = capabilities.submitCoreReturn !== false;
  const canSubmitWarranty = capabilities.submitWarranty !== false;
  const canOpenWarrantyQueue =
    typeof hasPermission === "function" && hasPermission("sales");

  if (!token)
    return (
      <Navigate
        to="/authentication/sign-in"
        state={{ from: location }}
        replace
      />
    );
  if (authLoading || pluginRole == null) return null;
  if (!isPortal) return <Navigate to="/dashboard" replace />;
  if (!documentId) return <Navigate to="/account/valve-bodies" replace />;

  return (
    <CustomerPortalLayout
      title={detail?.partNumber || detail?.displayTag || t("registeredUnits.fallbackName", "Valve Body")}
      subtitle={
        distributor
          ? "Distributor sold/registration visibility."
          : "Registration, core return, and warranty view."
      }
      actions={
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <MDButton
            variant="outlined"
            color="info"
            onClick={() => navigate("/account/valve-bodies")}
          >
            Back
          </MDButton>
          {!distributor && lifecycle?.coreDue && canSubmitCore ? (
            <MDButton
              variant="outlined"
              color="info"
              onClick={() =>
                navigate(`/account/valve-bodies/${documentId}/core-return`)
              }
            >
              Return core
            </MDButton>
          ) : null}
          {!distributor && canSubmitWarranty && !hasActiveWarranty ? (
            <MDButton
              variant="contained"
              color="info"
              onClick={() =>
                navigate(`/account/valve-bodies/${documentId}/warranty`)
              }
            >
              Warranty claim
            </MDButton>
          ) : null}
          {!distributor && hasActiveWarranty && activeWarrantyDocumentId ? (
            <MDButton
              variant="contained"
              color="info"
              onClick={() =>
                navigate(`/account/warranty-claims/${activeWarrantyDocumentId}`)
              }
            >
              Open active RMA
            </MDButton>
          ) : null}
          {!distributor && hasActiveWarranty && activeWarrantyDocumentId ? (
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={() =>
                navigate(
                  `/account/warranty-claims/${activeWarrantyDocumentId}/chat`
                )
              }
            >
              Open claim chat
            </MDButton>
          ) : null}
        </Box>
      }
    >
      {detailQuery.error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {detailQuery.error?.response?.data?.error?.message ||
            detailQuery.error?.message ||
            t("registeredUnits.loadError", "Failed to load registered item.")}
        </Alert>
      ) : null}

      {detailQuery.isLoading ? (
        <Alert severity="info">
          {t("registeredUnits.loading", "Loading registered item...")}
        </Alert>
      ) : !detail ? (
        <Alert severity="warning">{t("registeredUnits.notFound", "Valve body not found.")}</Alert>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ p: 2 }}>
              <MDTypography variant="h6" fontWeight={900} gutterBottom>
                Lifecycle
              </MDTypography>
              <TinyRow
                label="Tag"
                value={detail?.tag || detail?.serialNumber}
              />
              <TinyRow label="Serial" value={detail?.serialNumber} />
              <TinyRow label="Part number" value={detail?.partNumber} />
              <TinyRow label="Product" value={detail?.productName} />
              <TinyRow
                label="Status"
                value={prettyStatus(lifecycle?.progressStatus)}
              />
              <TinyRow
                label="Registered"
                value={lifecycle?.registered ? "Yes" : "No"}
              />
              <TinyRow
                label="Core due"
                value={lifecycle?.coreDue ? "Yes" : "No"}
              />
              <TinyRow
                label="Core returned"
                value={lifecycle?.coreReturned ? "Yes" : "No"}
              />
              <TinyRow
                label="Disposition"
                value={prettyStatus(lifecycle?.disposition || "active")}
              />
              <TinyRow
                label="Rebuild count"
                value={String(lifecycle?.rebuildCount || 0)}
              />
              <TinyRow
                label="Resale count"
                value={String(lifecycle?.resaleCount || 0)}
              />
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ p: 2 }}>
              <MDTypography variant="h6" fontWeight={900} gutterBottom>
                Ownership
              </MDTypography>
              <TinyRow
                label="Sold to role"
                value={prettyStatus(lifecycle?.soldToRole || "customer")}
              />
              <TinyRow
                label="Sold to"
                value={lifecycle?.soldToCustomer?.name || "—"}
              />
              <TinyRow
                label="Registered customer"
                value={lifecycle?.registeredCustomer?.name || "—"}
              />
              <TinyRow label="Sold at" value={lifecycle?.soldAt || "—"} />
              <TinyRow
                label="Registered at"
                value={lifecycle?.registeredAt || "—"}
              />
              {lifecycle?.activeReturnRequest?.status ? (
                <TinyRow
                  label="Active return"
                  value={coreReturnStatusLabel(lifecycle)}
                />
              ) : null}
              {lifecycle?.activeWarranty?.rmaNumber ? (
                <TinyRow
                  label="Active RMA"
                  value={`${lifecycle.activeWarranty.rmaNumber} (${prettyStatus(
                    lifecycle.activeWarranty.status
                  )})`}
                />
              ) : null}
            </Card>
          </Grid>
          {!distributor && lifecycle?.coreDue ? (
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <MDTypography variant="h6" fontWeight={900}>
                      Refundable core credit
                    </MDTypography>
                    <MDTypography variant="h3" fontWeight={900} color="info">
                      {formatMoney(
                        lifecycle?.refundableCoreAmount || 0,
                        lifecycle?.currency
                      )}
                    </MDTypography>
                    <MDTypography
                      variant="body2"
                      color="text"
                      sx={{ opacity: 0.8 }}
                    >
                      {t(
                        "coreReturn.refundText",
                        "Once the return is received, inspected, and approved, the eligible amount is refunded."
                      )}
                    </MDTypography>
                  </Box>
                  <Chip
                    variant="outlined"
                    color="info"
                    label={coreReturnStatusLabel(lifecycle)}
                  />
                </Box>
                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} md={4}>
                    <TinyRow
                      label="Pending cores"
                      value={String(
                        lifecycle?.refundableCoreOutstandingQty || 0
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TinyRow
                      label="Per-core deposit"
                      value={formatMoney(
                        lifecycle?.refundableCoreCharge || 0,
                        lifecycle?.currency
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TinyRow
                      label="Deadline"
                      value={formatDateTime(lifecycle?.coreDueAt)}
                    />
                  </Grid>
                </Grid>
                {lifecycle?.activeReturnRequest ? (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={6}>
                        <TinyRow
                          label="Tracking"
                          value={
                            [
                              lifecycle.activeReturnRequest.shippingCarrier,
                              lifecycle.activeReturnRequest.trackingNumber,
                            ]
                              .filter(Boolean)
                              .join(" • ") || "—"
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TinyRow
                          label="Started"
                          value={formatDateTime(
                            lifecycle.activeReturnRequest.requestedAt
                          )}
                        />
                      </Grid>
                    </Grid>
                    {lifecycle?.warehouseInspectionNotes ? (
                      <MDTypography
                        variant="caption"
                        color="text"
                        sx={{
                          display: "block",
                          mt: 1,
                          opacity: 0.75,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {lifecycle.warehouseInspectionNotes}
                      </MDTypography>
                    ) : null}
                    <Box
                      sx={{
                        mt: 1.5,
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      {lifecycle?.activeReturnRequest?.shippingLabelUrl ? (
                        <MDButton
                          component="a"
                          href={lifecycle.activeReturnRequest.shippingLabelUrl}
                          target="_blank"
                          rel="noreferrer"
                          variant="outlined"
                          color="info"
                        >
                          View return label
                        </MDButton>
                      ) : null}
                      {lifecycle?.activeReturnRequest
                        ?.shippingProviderTrackUrl ? (
                        <MDButton
                          component="a"
                          href={
                            lifecycle.activeReturnRequest
                              .shippingProviderTrackUrl
                          }
                          target="_blank"
                          rel="noreferrer"
                          variant="outlined"
                          color="secondary"
                        >
                          Track return
                        </MDButton>
                      ) : null}
                    </Box>
                  </>
                ) : null}
              </Card>
            </Grid>
          ) : null}
          {!distributor ? (
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <MDTypography variant="h6" fontWeight={900} gutterBottom>
                  Warranty history
                </MDTypography>
                {historyQuery.isLoading ? (
                  <MDTypography variant="button">Loading history…</MDTypography>
                ) : !warrantyHistory.length ? (
                  <Alert severity="info">No warranty claims yet.</Alert>
                ) : (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {warrantyHistory.map((row) => (
                      <Card
                        key={String(row?.documentId || row?.id)}
                        variant="outlined"
                        sx={{ p: 1.75, borderRadius: 2 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <MDTypography variant="button" fontWeight={800}>
                            {row?.rmaNumber || row?.documentId}
                          </MDTypography>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={claimStatusColor(row?.status)}
                            label={prettyStatus(row?.status || "open")}
                          />
                        </Box>
                        <MDTypography
                          variant="caption"
                          display="block"
                          color="text"
                          sx={{ opacity: 0.75, mt: 0.5 }}
                        >
                          {prettyStatus(row?.requestType)} • Claim #
                          {Number(row?.claimSequence || 1)} • Updated{" "}
                          {formatDateTime(row?.updatedAt || row?.createdAt)}
                        </MDTypography>
                        {row?.supportRequired ? (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            {row?.supportMessage ||
                              "Customer service contact required before next step."}
                          </Alert>
                        ) : null}
                        <Grid container spacing={1} sx={{ mt: 0.5 }}>
                          <Grid item xs={12} md={6}>
                            <MDTypography
                              variant="caption"
                              color="text"
                              sx={{ opacity: 0.75 }}
                            >
                              Vehicle
                            </MDTypography>
                            <MDTypography variant="button" display="block">
                              {[
                                row?.vehicleYear,
                                row?.vehicleMake,
                                row?.vehicleModel,
                                row?.vehicleTrim,
                              ]
                                .filter(Boolean)
                                .join(" ") || "—"}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text"
                              sx={{ opacity: 0.75 }}
                            >
                              VIN: {nonEmpty(row?.vehicleVin) || "—"}
                            </MDTypography>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <MDTypography
                              variant="caption"
                              color="text"
                              sx={{ opacity: 0.75 }}
                            >
                              Shipping
                            </MDTypography>
                            <MDTypography variant="button" display="block">
                              Inbound: {nonEmpty(row?.inboundCarrier) || "—"}{" "}
                              {nonEmpty(row?.inboundTrackingNumber)
                                ? `• ${row.inboundTrackingNumber}`
                                : ""}
                            </MDTypography>
                            <MDTypography variant="button" display="block">
                              Outbound: {nonEmpty(row?.outboundCarrier) || "—"}{" "}
                              {nonEmpty(row?.outboundTrackingNumber)
                                ? `• ${row.outboundTrackingNumber}`
                                : ""}
                            </MDTypography>
                          </Grid>
                        </Grid>
                        {normalizeIssueCodes(row?.issueCodes).length ? (
                          <Box
                            sx={{
                              mt: 1,
                              display: "flex",
                              gap: 0.75,
                              flexWrap: "wrap",
                            }}
                          >
                            {normalizeIssueCodes(row?.issueCodes).map(
                              (code) => (
                                <Chip
                                  key={`${row?.documentId || row?.id}-${code}`}
                                  label={code}
                                  size="small"
                                />
                              )
                            )}
                          </Box>
                        ) : null}
                        {nonEmpty(row?.issueDescription) ? (
                          <MDTypography
                            variant="body2"
                            sx={{ mt: 1, whiteSpace: "pre-wrap" }}
                          >
                            {row.issueDescription}
                          </MDTypography>
                        ) : null}
                        {nonEmpty(row?.notes) ? (
                          <MDTypography
                            variant="caption"
                            color="text"
                            sx={{
                              mt: 0.75,
                              display: "block",
                              opacity: 0.75,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            Notes: {row.notes}
                          </MDTypography>
                        ) : null}
                        <Divider sx={{ my: 1 }} />
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {workflowStepsForClaim(row).map((step) => (
                            <Box
                              key={`${row?.documentId || row?.id}-${step.key}`}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Icon
                                color={step.complete ? "success" : "disabled"}
                                fontSize="small"
                              >
                                {step.complete
                                  ? "check_circle"
                                  : "radio_button_unchecked"}
                              </Icon>
                              <MDTypography
                                variant="caption"
                                color="text"
                                sx={{ opacity: step.complete ? 0.95 : 0.7 }}
                              >
                                {step.label}{" "}
                                {step.at ? `• ${formatDateTime(step.at)}` : ""}
                              </MDTypography>
                            </Box>
                          ))}
                        </Box>
                        <Box
                          sx={{
                            mt: 1,
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <MDButton
                            component={RouterLink}
                            to={`/account/warranty-claims/${encodeURIComponent(
                              String(row?.documentId || "")
                            )}/chat`}
                            variant="text"
                            color="secondary"
                            size="small"
                            sx={{ px: 0, mr: 1 }}
                          >
                            Chat
                          </MDButton>
                          <MDButton
                            component={RouterLink}
                            to={`/account/warranty-claims/${encodeURIComponent(
                              String(row?.documentId || "")
                            )}`}
                            variant="text"
                            color="secondary"
                            size="small"
                            sx={{ px: 0, mr: canOpenWarrantyQueue ? 1 : 0 }}
                          >
                            Claim details
                          </MDButton>
                          {canOpenWarrantyQueue ? (
                            <MDButton
                              component={RouterLink}
                              to={`/sales/warranty-claims?unit=${encodeURIComponent(
                                String(documentId || "")
                              )}&claim=${encodeURIComponent(
                                String(row?.documentId || "")
                              )}`}
                              variant="text"
                              color="info"
                              size="small"
                              sx={{ px: 0 }}
                            >
                              Open in claims queue
                            </MDButton>
                          ) : null}
                        </Box>
                      </Card>
                    ))}
                  </Box>
                )}
              </Card>
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ p: 2 }}>
              <MDTypography variant="h6" fontWeight={900} gutterBottom>
                Labels
              </MDTypography>
              <MDTypography variant="body2" color="text" sx={{ opacity: 0.8 }}>
                Registration QR:{" "}
                {detail?.instructionLabelData?.qr?.registrationUrl || "—"}
              </MDTypography>
              <MDTypography variant="body2" color="text" sx={{ opacity: 0.8 }}>
                Distributor sold QR:{" "}
                {detail?.packagingLabelData?.qr?.distributorSoldUrl || "—"}
              </MDTypography>
            </Card>
          </Grid>
        </Grid>
      )}
    </CustomerPortalLayout>
  );
}
